import { ISlide } from '@/components/admin/SlideShow/stores/useSlideShowStore';

// One entry of the sidebar's display list (store.slides): a slide, a slide group card, or the synthetic final
// card ({ id: '0', type: 'Transition' }) that has no __typename.
export interface DisplayItem {
  __typename?: string;
  id: number | string;
  position: number;
  slideGroupId?: number | null;
  type?: string;
}

type DraggableKind = 'group' | 'slide';

// eslint-disable-next-line no-underscore-dangle
const isGroupCard = (item: DisplayItem) => item.__typename === 'SlideGroup';
export const isTransition = (item: DisplayItem) => item.type === 'Transition';

export const makeDraggableId = (kind: DraggableKind, id: number | string) => `${kind}-${id}`;

export const parseDraggableId = (draggableId: string): { id: string; kind: DraggableKind } => {
  const separator = draggableId.indexOf('-');
  const kind = draggableId.slice(0, separator) as DraggableKind;

  return { id: draggableId.slice(separator + 1), kind };
};

const sameId = (a: number | string, b: number | string) => String(a) === String(b);

interface SlideReorderArgs {
  destinationPosition: number;
  displayItems: DisplayItem[];
  slideId: number | string;
  slides: ISlide[];
}

interface SlideReorderParams {
  dbPosition: number;
  slideGroupId: number | null;
  slides: ISlide[];
}

// Converts a drop in the display list (slides interleaved with group cards, 1-based) into the DB slide
// position + slide group the REORDER_SLIDE mutation needs, plus the optimistic raw slide list.
export const getSlideReorderParams = ({
  destinationPosition,
  displayItems,
  slideId,
  slides,
}: SlideReorderArgs): SlideReorderParams | null => {
  const dragged = displayItems.find(item => !isTransition(item) && sameId(item.id, slideId));
  if (!dragged || destinationPosition === dragged.position) return null;

  const groupCards = displayItems.filter(isGroupCard);
  const cardsAbove = groupCards.filter(card => card.position < destinationPosition);
  let offset = cardsAbove.length;
  let groupCard = cardsAbove[cardsAbove.length - 1];

  // Moving down onto a card's slot lands directly below that card, i.e. as the first slide of its group.
  const displaced = displayItems.find(item => item.position === destinationPosition);
  if (destinationPosition > dragged.position && displaced && isGroupCard(displaced)) {
    offset += 1;
    groupCard = displaced;
  }

  const currentGroupId = dragged.slideGroupId ?? null;
  const currentGroupHasOtherSlides = currentGroupId !== null && slides.some(
    slide => !sameId(slide.id, slideId) && slide.slideGroupId != null && sameId(slide.slideGroupId, currentGroupId),
  );
  const targetGroupId = groupCard ? Number(groupCard.id) : null;
  // A group's only slide keeps its group, so a card never disappears from the sidebar because of a drag.
  const keepsGroup = currentGroupId !== null && !currentGroupHasOtherSlides;
  const slideGroupId = keepsGroup ? currentGroupId : targetGroupId;

  const dbPosition = destinationPosition - offset;
  const remaining = [...slides].sort((a, b) => a.position - b.position).filter(slide => !sameId(slide.id, slideId));
  const moved = slides.find(slide => sameId(slide.id, slideId));
  remaining.splice(dbPosition - 1, 0, { ...moved, slideGroupId });

  return {
    dbPosition,
    slideGroupId,
    slides: remaining.map((slide, index) => ({ ...slide, position: index + 1 })),
  };
};
